import React, {useState, useEffect, useCallback} from 'react';

import {
  DataGrid
} from "@mui/x-data-grid";
import {Grid} from "@mui/material";
import {showCurrency} from "../dashboard/Dashboard";
import {getData} from "../util/__fetch";
import FNOUser from "../fno/FNOUser";
import NewWindowPortal from "../fno/NewWindowPortal";

// show user name, broker, pnl, count of open positions, availableAmount, totalAmount

const AllUsersPnL = ({user, allUsers, allPositions, customToolBar, getClassName}) => {
  const [allUsersCash, setAllUsersCash] = useState([]);
  const [openUserIds, setOpenUserIds] = useState([]);

  const openInPortal = (e, userId) => {
    e.preventDefault();
    setOpenUserIds(prev => prev.includes(userId) ? prev : [...prev, userId]);
  }

  const closePortal = (userId) => {
    setOpenUserIds(prev => prev.filter(id => id !== userId));
  }

  const pnlColumns = [
    {
      field: 'userName', headerName: 'User', width: 150, type: 'string', renderCell: (params) => (
        <a href="#" onClick={e => openInPortal(e, params?.row?.userId)}>
          {params?.value}
        </a>
      )
    },
    {
      field: 'pnl', headerName: 'P&L', width: 80, type: 'number', valueFormatter: (params) => {
        return params?.value ? showCurrency(params?.value, false) : '';
      }
    },
    {
      field: 'pnlPercentage', headerName: 'P&L %', width: 90, type: 'number', valueGetter: (params) => {
        const totalAmount = params?.row?.totalAmount;
        return totalAmount ? ((params?.row?.pnl / totalAmount) * 100).toFixed(2) : 0;
      }, valueFormatter: (params) => {
        return params?.value ? showCurrency(params?.value, false) : '';
      }
    }, {field: 'openPositionsCount', headerName: 'Open #', width: 80, type: 'number'}, {
      field: 'availableAmount',
      headerName: 'Available Amount',
      width: 140,
      type: 'number',
      valueGetter: (params) => params?.row?.totalAmount - params?.row?.usedAmount,
      valueFormatter: (params) => {
        return params?.value ? showCurrency(params?.value, false) : '';
      }
    }, {
      field: 'totalAmount', headerName: 'Total Amount', width: 140, type: 'number', valueFormatter: (params) => {
        return params?.value ? showCurrency(params?.value, false) : '';
      }
    }, {
      field: 'broker',
      headerName: 'Broker',
      width: 100,
      type: 'string',
    },]

  // console.log('All users P&L', allUsers, allUsersCash);
  const fetchUserName = useCallback((userId) => {
    const userData = allUsers?.find(u => u.userId === userId);
    return userData ? userData.fullName : userId;
  }, [allUsers])

  const addUserName = useCallback((allUsersPortfolioAmounts) => {
    const rowsWithUserName = allUsersPortfolioAmounts?.map((value, index) => ({
      ...value,
      userName: fetchUserName(value.userId),
    }))
    setAllUsersCash(rowsWithUserName)
  }, [fetchUserName])

  useEffect(() => {
    const allUserIdsCsv = Array.isArray(allUsers) ? allUsers.map(u => u.userId).join(',') : '';
    getData('/algo-trading/api/fno/portfolio/available-cash/all?allUserIdsCsv=' + allUserIdsCsv, user, addUserName, (() => {
    }), (() => {
    }))
      .then(r => {
      })
  }, [user, allUsers, addUserName]);

  function groupByAndSum(arr, key, sumKey, sideKey) {
    return arr?.reduce((acc, curr) => {
      const groupKey = curr[key];
      if (!acc[groupKey]) {
        acc[groupKey] = {[key]: groupKey, [sideKey]: curr[sideKey], [sumKey]: 0};
      }
      acc[groupKey][sumKey] += curr[sumKey];
      return acc;
    }, {});
  }

  useEffect(() => {
    const onlyOpenFNOPositions = allPositions
      .filter(data => data?.position?.netqty !== 0)
      .filter(data => data?.position?.exchange === 'NFO' || data?.position?.exchange === 'BFO');

    const onlyClosedFNOPositions = allPositions
      ?.filter(data => data?.position?.netqty === 0)
      ?.filter(data => data?.position?.exchange === 'NFO' || data?.position?.exchange === 'BFO');
     // console.log('open', onlyOpenFNOPositions,'closed positions', onlyClosedFNOPositions, groupByAndSum(onlyClosedFNOPositions, 'user', 'count', 'broker'));
    if (onlyOpenFNOPositions?.length === 0 && onlyClosedFNOPositions?.length === 0) {
      return;
    }
    const openPositionsCountByUser = onlyOpenFNOPositions?.reduce((acc, pos) => {
      const userId = pos?.user;
      acc[userId] = (acc[userId] || 0) + 1;
      return acc;
    }, {});
    // console.log('openPositionsCountByUser', openPositionsCountByUser);

    const extractRelentFields = allPositions.map(data => ({
      userId: data?.user,
      broker: data?.broker,
      pnl: data?.position?.pnl,
    }))
    const groupedData = groupByAndSum(extractRelentFields, 'userId', 'pnl', 'broker');
    // console.log('groupedData', groupedData);
    // merge allUsersCash and groupedData arrays based on user ids
    const mergedData = allUsersCash.map(userCash => {
      const userId = userCash.userId;
      if (groupedData[userId]) {
        return {
          ...userCash,
          ...groupedData[userId],
          openPositionsCount: openPositionsCountByUser[userCash?.userId] || 0
        };
      }
      else {
        userCash.openPositionsCount = openPositionsCountByUser[userCash?.userId] || 0;
      }
      return userCash;
    });
    // console.log('mergedData', mergedData);
    // Only update if data changed
    if (JSON.stringify(mergedData) !== JSON.stringify(allUsersCash)) {
      setAllUsersCash(mergedData);
    }
    // add this to existing rows
  }, [allPositions, allUsersCash]);

  return (
    <Grid item>
      {openUserIds.map(userId => {
        const userObj = allUsers.find(u => u.userId === userId);
        const userName = userObj ? userObj.fullName : userId;
        return (
          <NewWindowPortal key={userId} title={userName} onClose={() => closePortal(userId)}>
            <FNOUser user={user} userId={userId} userName={userName}/>
          </NewWindowPortal>
        );
      })}
      <DataGrid
        key={"1"}
        getRowId={(row) => row?.userId}
        columns={pnlColumns}
        rows={allUsersCash}
        autoHeight={true}
        pageSizeOptions={[5, 10, 20, 50]}
        initialState={{
          sorting: {
            sortModel: [{field: 'totalAmount', sort: 'desc'}]
          }, pagination: {
            paginationModel: {page: 0, pageSize: 10},
          },
        }}
        slots={{
          toolbar: () => customToolBar('P&L'),
        }}
        getCellClassName={(params) => {
          if (params.field !== 'pnl' && params.field !== 'pnlPercentage') {
            return '';
          }
          return getClassName(params, params.field)
        }}
      />
    </Grid>
  )
}

export default AllUsersPnL;
